import type { ChangeEvent, ReactNode } from 'react'
import { forwardRef } from 'react'
import type {
  FormControlProps,
  GridProps} from '@chakra-ui/react'
import {
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react'

import { HelperTooltip } from '../ui/HelperTooltip'
import type { Document } from 'BrokerTsLib/src/dtos'
import { useUrl } from '@/hooks'
import { Apis } from '@/appSettings'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'

export interface RecapExtraRowProps extends GridProps {
  name: string | ReactNode
  checkboxLabel: string
  isEnabled?: boolean
  isDisabled?: boolean
  onEnabledChange?: (isEnabled: boolean) => void
  helpText?: string
  price?: string
  sidebar?: ReactNode
  isLast?: boolean
  documents?: Document[]
}

interface RecapExtraRowBodyProps extends GridProps {
  isLast?: boolean
}

const RecapExtraRowBody = forwardRef(
  ({ children, sx, isLast, ...rest }: RecapExtraRowBodyProps, ref) => {
    return (
      <Grid
        ref={ref}
        sx={{
          mx: {
            base: 0,
            lg: 6,
          },
          ml: {
            base: 6,
          },
          mr: {
            base: 6,
            lg: 0,
          },
          px: 2,
          py: 4,
          gap: 4,
          alignItems: 'center',
          borderBottom: isLast ? '3px double' : '1px solid',
          borderColor: 'neutral.50',
          ...sx,
        }}
        {...rest}
      >
        {children}
      </Grid>
    )
  },
)

export const RecapExtraRowFormControl = forwardRef(
  ({ children, sx, label, ...rest }: FormControlProps, ref) => {
    return (
      <FormControl ref={ref} sx={{ ...sx }} {...rest}>
        <FormLabel sx={{ fontSize: 'sm' }}>{label}</FormLabel>

        {children}
      </FormControl>
    )
  },
)

export const RecapExtraRow = forwardRef(
  (
    {
      children,
      name,
      checkboxLabel,
      isEnabled,
      isDisabled = false,
      onEnabledChange,
      helpText,
      price,
      sidebar,
      isLast = false,
      documents,
      sx,
      ...rest
    }: RecapExtraRowProps,
    ref,
  ) => {
    const documentUrl = useUrl(Apis.document)

    const handleEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked

      onEnabledChange?.(enabled)
    }

    return (
      <Grid
        ref={ref}
        sx={{
          alignItems: 'start',
          gridTemplateColumns: { base: '1fr', lg: 'auto 278px' },
          gap: 4,
          borderBottom: '1px solid',
          borderBottomColor: 'neutral.50',
          _last: {
            borderBottom: 'none',
          },
          ...sx,
        }}
        {...rest}
      >
        <RecapExtraRowBody isLast={isLast}>
          <Text sx={{ fontSize: 'sm' }}>{name}</Text>

          <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Flex
              sx={{
                gap: 4
              }}
            >
              <Checkbox
                sx={{ fontSize: 'sm' }}
                isChecked={isEnabled}
                isDisabled={isDisabled}
                onChange={handleEnabledChange}
              >
                {checkboxLabel}
              </Checkbox>

              {helpText && <HelperTooltip label={helpText} />}
            </Flex>

            <Flex alignItems='center' gap={8}>
              {(documents && documents.length > 0) &&
                <Menu size="sm" placement='bottom'>
                  <MenuButton sx={{ gap: 2, whiteSpace: 'nowrap' }} type='button'>
                    XML
                  </MenuButton>
                  <MenuList>
                    {documents.map(doc => (
                      <MenuItem key={doc.id} as={'a'} target='_blank' href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}>
                        {doc.name} ({formatDateTime(doc.date, 'd.M.yyyy HH:mm:ss.SSS')})
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              }

              {price && (
                <>
                  <Text sx={{ fontSize: 'sm', fontWeight: 'bold' }}>{price}</Text>
                </>
              )}
            </Flex>
          </Flex>

          <Flex sx={{ gap: 4, pt: 4, flexWrap: 'wrap' }}>{children}</Flex>
        </RecapExtraRowBody>

        {sidebar && sidebar}
      </Grid>
    )
  },
)

